// Canonical CardContext (cards_version 2) — what the agent should treat as "the card on screen".
// Built from the backend card catalogue (GET /api/v1/configurations/client → data.cards) plus the
// latest `card.open` event for that card; sent inside every agent.context_update as `card_context`.
// Backend counterpart: api-service/app/services/llm/cards/instances.py (normalize_card_context).
import type { CardDefinition, CardRowChildren } from '@/redux/internal-services/agentApi';
import type { SamCardEvent } from '@/components/SAM/samActionTypes';

export interface CardContextItem {
  index: number;
  entity_id: string;
  title?: string;
  status?: string;
  /** Configured row fields (sam_cards.context_fields) — scalar values as strings. */
  fields?: Record<string, string>;
  /** Nested records (catalogue `row_children`) — numbered as one running list across all rows. */
  children?: CardContextChild[];
}

export interface CardContextChild {
  index: number;
  entity_id: string;
  title?: string;
  status?: string;
}

export interface CardContext {
  active_card: {
    card_instance_id: string | null;
    card_definition_id: string;
    title?: string;
    semantic_state: { filter: Record<string, unknown> };
    visible_items: CardContextItem[];
    selected_entity: { index?: number; entity_id: string } | null;
    parent_context: { type: string; id: string } | null;
  };
  open_cards: { card_definition_id: string }[];
}

const MAX_ITEMS = 50;

/** Pick the configured `paths` (dotted allowed) from a row record; scalars (or scalar lists) only, empty skipped. */
export function pickRowFields(rec: Record<string, unknown> | undefined, paths: string[] | undefined) {
  const out: Record<string, string> = {};
  for (const path of paths ?? []) {
    const v = path.split('.').reduce<unknown>(
      (o, k) => (o && typeof o === 'object' ? (o as Record<string, unknown>)[k] : undefined), rec);
    // A list of scalars (role names, tags) is joined; objects and object lists are skipped.
    const val = Array.isArray(v) && v.every(x => x === null || typeof x !== 'object') ? v.filter(x => x != null && x !== '').join(', ') : v;
    if (val !== undefined && val !== null && val !== '' && typeof val !== 'object') out[path] = String(val);
  }
  return Object.keys(out).length ? out : undefined;
}

/** The row's nested records per the catalogue spec; `start` continues the running index. */
export function pickRowChildren(
  rec: Record<string, unknown> | undefined, spec: CardRowChildren | null | undefined, start: number,
): CardContextChild[] {
  const list = spec && rec ? rec[spec.path] : undefined;
  if (!spec || !Array.isArray(list)) return [];
  const out: CardContextChild[] = [];
  for (const raw of list) {
    if (!raw || typeof raw !== 'object') continue;
    const r = raw as Record<string, unknown>;
    const id = r[spec.id];
    if (id === undefined || id === null || id === '') continue;
    const title = spec.title ? r[spec.title] : undefined;
    const status = spec.status ? r[spec.status] : undefined;
    out.push({
      index: start + out.length + 1,
      entity_id: String(id),
      ...(title ? { title: String(title) } : {}),
      ...(status ? { status: String(status) } : {}),
    });
  }
  return out;
}

function canonical(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(canonical).join(',')}]`;
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    // An `undefined` value means "not set" — same as an absent key.
    return `{${Object.keys(o).filter(k => o[k] !== undefined).sort()
      .map(k => `${JSON.stringify(k)}:${canonical(o[k])}`).join(',')}}`;
  }
  return JSON.stringify(v ?? null);
}

/** The server `card.open` event for this exact view, or undefined. An event only counts when it is
 *  for the same card definition AND the same record. A list/panel card (`recordId` null) must also
 *  have the same filter (key-order-insensitive) — a list the user re-filtered by hand is a different
 *  instance than the one SAM opened. A `view` card is identified by its record alone: the list
 *  filter behind it says nothing about which record is open. */
export function matchingCardEvent(
  event: SamCardEvent | undefined, definitionId: string,
  filter: Record<string, unknown> | undefined, recordId: string | null = null,
): SamCardEvent | undefined {
  if (!event || event.card_definition_id !== definitionId) return undefined;
  if ((event.record_id ?? null) !== recordId) return undefined;
  if (recordId !== null) return event;
  return canonical(event.semantic_state.filter ?? {}) === canonical(filter ?? {}) ? event : undefined;
}

/** The enabled card a module is showing: its `view` card while a record is open, else its
 *  list/panel card. Undefined when the catalogue hasn't loaded or knows no card for the module. */
export function resolveCardDefinition(
  cards: CardDefinition[] | undefined, module: string, openedId: string | null | undefined,
): CardDefinition | undefined {
  const mine = (cards ?? []).filter(c => c.module === module && c.enabled && c.status === 'active');
  if (!mine.length) return undefined;
  const want = openedId ? ['view'] : ['list', 'panel'];
  return mine.find(c => want.includes(c.presentation)) ?? mine[0];
}

function parentFromFilter(filter: Record<string, unknown>): CardContext['active_card']['parent_context'] {
  const type = filter.context_type;
  const id = filter.context_entity_id ?? filter.entity_id;
  if (typeof type === 'string' && (typeof id === 'string' || typeof id === 'number')) {
    return { type, id: String(id) };
  }
  if (typeof filter.initial_engagement_id === 'string') {
    return { type: 'engagement', id: filter.initial_engagement_id };
  }
  return null;
}

export function buildCardContext(opts: {
  definition: CardDefinition;
  event?: SamCardEvent;
  items: { index: number; id: string; title?: string; status?: string }[];
  /** Row records aligned with `items` (same order) — source of the configured fields. */
  records?: Record<string, unknown>[];
  /** sam_cards.context_fields for this card definition. */
  fieldPaths?: string[];
  openedId: string | null | undefined;
  filter: Record<string, unknown> | undefined;
  title?: string;
  openCardIds?: string[];
}): CardContext {
  const { definition, items, openedId, title } = opts;
  const filter = opts.filter ?? {};
  const event = matchingCardEvent(
    opts.event, definition.id, filter, definition.presentation === 'view' ? (openedId ?? null) : null,
  );
  let childCount = 0;
  const visible = items.slice(0, MAX_ITEMS).map((it, i) => {
    const fields = pickRowFields(opts.records?.[i], opts.fieldPaths);
    const children = pickRowChildren(opts.records?.[i], definition.row_children, childCount);
    childCount += children.length;
    return {
      index: it.index,
      entity_id: String(it.id),
      ...(it.title ? { title: it.title } : {}),
      ...(it.status ? { status: it.status } : {}),
      ...(fields ? { fields } : {}),
      ...(children.length ? { children } : {}),
    };
  });
  const selected = openedId ? visible.find(v => v.entity_id === openedId) : undefined;
  return {
    active_card: {
      card_instance_id: event?.card_instance_id ?? null,
      card_definition_id: definition.id,
      ...(title ? { title } : {}),
      semantic_state: { filter },
      visible_items: visible,
      selected_entity: openedId ? { ...(selected ? { index: selected.index } : {}), entity_id: openedId } : null,
      parent_context: event?.semantic_state.parent_context ?? parentFromFilter(filter),
    },
    open_cards: (opts.openCardIds ?? []).map(id => ({ card_definition_id: id })),
  };
}
